import {VariantProps} from 'cva';
import React from 'react';
import {asREM, cva, styles} from '../../Utilities';
import {FieldClassName} from '../_internal/Field/Field.classes';
import {FieldLayout} from '../_internal/Field/FieldLayout';
import {FieldLabel} from '../_internal/FieldLabel/FieldLabel';
import {FieldLabelProps} from '../_internal/FieldLabel/FieldLabel.types';
import {RsnSkeleton} from '../SkeletalElements/Skeleton';

interface RsnTextareaProps
    extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, keyof React.HTMLAttributes<never>>,
        Partial<FieldLayout.Props>,
        VariantProps<typeof cls.textarea> {
    textareaRef?: React.Ref<HTMLTextAreaElement>;
    inputMode?: React.HTMLAttributes<any>['inputMode'];

    onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
    value?: string;

    onBlur?: React.TextareaHTMLAttributes<HTMLTextAreaElement>['onBlur'];
    onFocus?: React.TextareaHTMLAttributes<HTMLTextAreaElement>['onFocus'];
}

export function RsnTextarea({
    label,
    decoration,
    helperText,
    issue,
    isInvalid = false,
    textareaRef,
    ...props
}: RsnTextareaProps) {
    if (typeof label === 'string') {
        label = <RsnTextarea.Label>{label}</RsnTextarea.Label>;
    }
    return (
        <FieldLayout
            decoration={decoration}
            label={label}
            issue={issue}
            helperText={helperText}
            isInvalid={isInvalid}
            isDisabled={props.disabled}
        >
            <textarea {...props} ref={textareaRef} className={cls.textarea(props)} />
        </FieldLayout>
    );
}

const cls = styles({
    textarea: cva(FieldClassName({height: 'unset', class: 'py-3'}), {
        variants: {
            resizable: {
                false: 'resize-none',
                true: 'resize',
                y: 'resize-y',
                x: 'resize-x',
            },
        },
        defaultVariants: {
            resizable: false,
        },
    }),
});

export namespace RsnTextarea {
    export type Props = RsnTextareaProps;
    export type LabelProps = FieldLabelProps;
}

RsnTextarea.Label = FieldLabel.bind(null);
RsnTextarea.Label.displayName = 'RsnTextarea.Label';

const Skeleton = ({rows = 2}: {rows?: number}) => (
    <div className='grid gap-2'>
        <RsnSkeleton width={32} height={12} />
        <div
            className='box-content flex gap-2 rounded-sm border border-solid border-neutral-40 p-3'
            style={{height: asREM(20 * rows)}}
        >
            <RsnSkeleton width={100} height={12} />
        </div>
    </div>
);
RsnTextarea.Skeleton = Skeleton;
Skeleton.displayName = 'RsnTextarea.Skeleton';
